import React, { useState, useEffect, useRef } from "react";
import { BsBookmark, BsBookmarkFill } from "react-icons/bs";
import { ImSpellCheck } from "react-icons/im";
import { motion } from "framer-motion";
import 'katex/dist/katex.min.css';
import Latex from "react-latex-next";
import { MdOutlineTimer } from "react-icons/md";

const Question = ({
    time,
    sat,
    handleAnswerValueChange,
    handleCrossOutOptions,
    handleSelectOption,
    handleBookMark,
    currentQuestion,
    result,
    questionOverViewOpen,
    hideBookmark
}) => {
    const [showCrossOutMenu, setShowCrossOutMenu] = useState(false);
    const [crossedOutOptions, setCrossedOutOptions] = useState([]);
    const [isKatexRendered, setIsKatexRendered] = useState(false);

    // State for pane widths
    const [leftWidth, setLeftWidth] = useState(50); // Start with 50% width
    const [isResizing, setIsResizing] = useState(false);

    // Refs
    const dividerRef = useRef(null);

    // Event Handlers for Resizing
    const onMouseDown = (e) => {
        if (questionOverViewOpen === false) {
            setIsResizing(true);
        }
    };

    const onMouseMove = (e) => {
        if (!isResizing) return;
        const containerWidth = dividerRef.current.parentNode.getBoundingClientRect().width;
        const newLeftWidth = (e.clientX / containerWidth) * 100;
        if (newLeftWidth > 10 && newLeftWidth < 90) {
            setLeftWidth(newLeftWidth);
        }
    };

    const onMouseUp = () => {
        setIsResizing(false);
    };

    // Add event listeners
    useEffect(() => {
        if (isResizing) {
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
            document.body.style.userSelect = "none"; // Prevent text selection
        } else {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            document.body.style.userSelect = "auto";
        }

        // Cleanup
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            document.body.style.userSelect = "auto";
        };
    }, [isResizing]);

    // Existing useEffect hooks
    useEffect(() => {
        if (result !== undefined) {
            // Perform any additional actions needed when result changes
        }
    }, [result]);

    useEffect(() => {
        const script2 = document.createElement("script");
        script2.src =
            "https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js";
        script2.integrity =
            "sha384-+VBxd3r6XgURycqtZ117nYw44OOcIax56Z4dCRWbxyPt0Koah1uHoK0o4+/RRE05";
        script2.crossOrigin = "anonymous";
        script2.defer = true;

        // Handle script load
        script2.onload = () => {
            const elements = document.querySelectorAll(".box-item-left, .stem-content");
            elements.forEach((element) => {
                if (window.renderMathInElement) {
                    window.renderMathInElement(element, {
                        delimiters: [
                            { left: "$$", right: "$$", display: false },
                            { left: "$", right: "$", display: false },
                            { left: "\\(", right: "\\)", display: false },
                            { left: "\\[", right: "\\]", display: true },
                            { left: "\\begin{array}", right: "\\end{array}", display: true },
                        ],
                        throwOnError: false,
                    });
                }
            });
            setIsKatexRendered(true);
        };
        document.body.appendChild(script2);

        // Cleanup: remove the script when component unmounts
        return () => {
            document.body.removeChild(script2);
        };
    }, [currentQuestion, result]);

    return (<>
        {currentQuestion.a !== null && currentQuestion.a !== "null" ? (
            // PC 버전: 좌우 레이아웃 (현재 코드 그대로)
            <div className="hidden md:flex h-[calc(100vh-150px)]">
                {/* Left Pane */}
                <div className="left-pane overflow-x-hidden" style={{ width: `${leftWidth}%` }}>
                    <div className="mx-[34px] mb-[160px] mt-[3%] z-[10] max-w-full">
                        <div className="flex flex-col justify-start ml-2 mr-2 box-item-left max-w-full">
                            <div className="max-w-full" data-highlightable="true">
                                {sat === "Math" || sat === "math" ? (
                                    <div
                                        id="test-imgsat"
                                        dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                                        className="m-4 min-h-[40%] overflow-y-auto my-list-style stem-content scrollbar-hide max-w-full"
                                    />
                                ) : (
                                    <div
                                        dangerouslySetInnerHTML={{ __html: currentQuestion.stimulus }}
                                        className="m-4 min-h-[40%] overflow-y-auto my-list-style stem-content max-w-full"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div ref={dividerRef}
                    className="divider mt-[20px] h-1/2 bg-gray-300 cursor-col-resize"
                    onMouseDown={onMouseDown} />

                {/* Right Pane */}
                <div
                    className="right-pane ml-[15px] mr-[15px] m-4"
                    style={{ width: `${100 - leftWidth}%` }}
                >
                    <div
                        className="flex flex-row justify-between rounded-[12px] shadow-sm"
                        style={
                            result === true
                                ? { background: "linear-gradient(to right,rgb(194, 214, 203), #f0fdfa)" }
                                : result === false
                                    ? { background: "linear-gradient(to right, #fef2f2, #fff1f2)" }
                                    : { background: "linear-gradient(to right, #f8fafc, #f1f5f9)" }
                        }
                    >
                        <div className="py-2 flex flex-row gap-2">
                            <div className="text-gray-700 pl-6 flex flex-row items-center gap-1 font-medium">
                                Q{currentQuestion.index + 1}
                                {!hideBookmark && (
                                    <motion.div
                                        whileTap={{ scale: 0.97 }}
                                        onClick={() => handleBookMark(currentQuestion.questionIds)}
                                        className="m-1 cursor-pointer"
                                    >
                                        {currentQuestion.bookMarked ? (
                                            <BsBookmarkFill color="#eb2626" size="1.2rem" />
                                        ) : (
                                            <BsBookmark color="#64748b" size="1.2rem" />
                                        )}
                                    </motion.div>
                                )}
                                {!hideBookmark && (
                                    <div>
                                        <p>Mark For Review</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-row items-center pr-4">
                            <motion.div
                                whileTap={{ scale: 0.98 }}
                                className={`p-[5px] border-gray-300 border-[1px] rounded-md cursor-pointer hover:bg-gray-50 transition-colors ${showCrossOutMenu ? "border-blue-500 bg-blue-50 text-blue-600" : ""
                                    }`}
                                onClick={() => setShowCrossOutMenu((pre) => !pre)}
                            >
                                <ImSpellCheck className={showCrossOutMenu ? "text-blue-600" : "text-gray-600"} />
                            </motion.div>
                        </div>
                    </div>

                    <div className="my-2 min-w-[100px] overflow-y-auto" data-highlightable="true">
                        {result === true ? (
                            <div className="flex max-w-[200px] bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-[8px] p-2 mb-[10px] shadow-sm">
                                <MdOutlineTimer className="mt-[4px] ml-[5px] mr-[5px] text-emerald-600" />
                                <span className="font-medium">Time Spent: {Math.max(1, time ?? 0)}sec</span>
                            </div>
                        ) : result === false ? (
                            <div className="flex max-w-[200px] bg-rose-100 text-rose-800 border border-rose-200 rounded-[8px] p-2 mb-[10px] shadow-sm">
                                <MdOutlineTimer className="mt-[4px] ml-[5px] mr-[5px] text-rose-600" />
                                <span className="font-medium">Time Spent: {Math.max(1, time ?? 0)}sec</span>
                            </div>
                        ) : (
                            <span></span>
                        )}

                        {sat !== "Math" && sat !== "math" && (
                            <div>
                                <div
                                    className="box-item-left"
                                    dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                                />
                            </div>
                        )}

                        {/* Options 영역: currentQuestion.a가 존재하므로 옵션들을 보여줌 */}
                        <div>
                            <Option
                                handleCrossOutOptions={handleCrossOutOptions}
                                currentQuestion={currentQuestion}
                                handleSelectOption={handleSelectOption}
                                idx={0}
                                showCrossOutMenu={showCrossOutMenu}
                                crossedOutOptions={crossedOutOptions}
                                data={currentQuestion.a}
                                result={result}
                            />
                            <Option
                                handleCrossOutOptions={handleCrossOutOptions}
                                currentQuestion={currentQuestion}
                                handleSelectOption={handleSelectOption}
                                idx={1}
                                showCrossOutMenu={showCrossOutMenu}
                                crossedOutOptions={crossedOutOptions}
                                data={currentQuestion.b}
                                result={result}
                            />
                            <Option
                                handleCrossOutOptions={handleCrossOutOptions}
                                currentQuestion={currentQuestion}
                                handleSelectOption={handleSelectOption}
                                idx={2}
                                showCrossOutMenu={showCrossOutMenu}
                                crossedOutOptions={crossedOutOptions}
                                data={currentQuestion.c}
                                result={result}
                            />
                            <Option
                                handleCrossOutOptions={handleCrossOutOptions}
                                currentQuestion={currentQuestion}
                                handleSelectOption={handleSelectOption}
                                idx={3}
                                showCrossOutMenu={showCrossOutMenu}
                                crossedOutOptions={crossedOutOptions}
                                data={currentQuestion.d}
                                result={result}
                            />
                        </div>

                        <div className={`ml-[15px] w-[550px] p-4 rounded-xl z-[99999] mx-4`} style={{ maxWidth: "550px" }}>
                            {result === true ? (
                                <>
                                    <div
                                        className="mt-[15px] box-item-left p-4 bg-emerald-50 border border-emerald-100 rounded-lg"
                                        dangerouslySetInnerHTML={{ __html: currentQuestion.rationale }}
                                    />
                                </>
                            ) : result === false ? (
                                <>
                                    <div
                                        className="mt-[15px] box-item-left p-4 bg-rose-50 border border-rose-100 rounded-lg"
                                        dangerouslySetInnerHTML={{ __html: currentQuestion.rationale }}
                                    />
                                </>
                            ) : (
                                <span></span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <div className="flex ml-[10%] justify-center">
                <div className="hidden md:flex h-full" style={{ width: `${leftWidth}%` }}>
                    {/* Right Pane */}
                    <div className="w-full">
                        <div
                            className="flex flex-row justify-between rounded-[12px] shadow-sm mb-[10px]"
                            style={
                                result === true
                                    ? { background: "linear-gradient(to right, #e0f2fe, #f0fdfa)" }
                                    : result === false
                                        ? { background: "linear-gradient(to right, #fef2f2, #fff1f2)" }
                                        : { background: "linear-gradient(to right, #f8fafc, #f1f5f9)" }
                            }
                        >
                            <div className="py-2 flex flex-row gap-2">
                                <div className="text-gray-700 pl-6 flex flex-row items-center gap-1 font-medium">
                                    Q{currentQuestion.index + 1}
                                    {!hideBookmark && (
                                        <motion.div
                                            whileTap={{ scale: 0.97 }}
                                            onClick={() => handleBookMark(currentQuestion.questionIds)}
                                            className="m-1 cursor-pointer"
                                        >
                                            {currentQuestion.bookMarked ? (
                                                <BsBookmarkFill color="#2563eb" size="1.2rem" />
                                            ) : (
                                                <BsBookmark color="#64748b" size="1.2rem" />
                                            )}
                                        </motion.div>
                                    )}
                                    {!hideBookmark && (
                                        <div>
                                            <p>Mark For Review</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {result === true ? (
                            <div className="flex bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-[8px] p-2 mb-[10px] shadow-sm">
                                <MdOutlineTimer className="mt-[4px] ml-[5px] mr-[5px] text-emerald-600" />
                                <span className="font-medium">Time Spent: {Math.max(1, time ?? 0)}sec</span>
                            </div>
                        ) : result === false ? (
                            <div className="flex bg-rose-100 text-rose-800 border border-rose-200 rounded-[8px] p-2 mb-[10px] shadow-sm">
                                <MdOutlineTimer className="mt-[4px] ml-[5px] mr-[5px] text-rose-600" />
                                <span className="font-medium">Time Spent: {Math.max(1, time ?? 0)}sec</span>
                            </div>
                        ) : (
                            <span></span>
                        )}
                        <div
                            id="test-imgsat"
                            className="mt-[20px] box-item-left bg-white p-4 rounded-lg shadow-sm"
                            dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                            data-highlightable="true"
                        />

                        <div className="my-4 min-w-[100px] overflow-y-auto" data-highlightable="true">
                            {sat !== "Math" && sat !== "math" && (
                                <div
                                    className={`p-4 rounded-lg mb-4 shadow-sm ${result === true
                                        ? "bg-emerald-50 border border-emerald-100"
                                        : result === false
                                            ? "bg-rose-50 border border-rose-100"
                                            : "bg-white border border-gray-200"
                                        }`}
                                >
                                    <div
                                        className="box-item-left"
                                        dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                                    />
                                </div>
                            )}

                            <div
                                className={`p-4 rounded-lg mb-[30px] ${result === true
                                    ? "shadow-sm bg-emerald-50 border border-emerald-100"
                                    : result === false
                                        ? "shadow-sm bg-rose-50 border border-rose-100"
                                        : ""
                                    }`}
                            >
                                <input
                                    className={`w-full py-2 px-3 outline-none rounded-md transition-colors duration-200 ${result === true
                                        ? "bg-white border-2 border-emerald-300 focus:border-emerald-500"
                                        : result === false
                                            ? "bg-white border-2 border-rose-300 focus:border-rose-500"
                                            : "border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                        }`}
                                    type="text"
                                    value={currentQuestion.selectedOption || ""}
                                    // result가 true/false인 경우에만 disabled
                                    disabled={result === true || result === false}
                                    onChange={(e) => {
                                        const cleaned = e.target.value.replace(/[^-0-9./]/g, "");
                                        // 혹시 여기서도 result가 true/false면 return 처리
                                        // if (result === true || result === false) return;
                                        handleAnswerValueChange(currentQuestion.questionIds, cleaned);
                                    }}
                                />


                                <div className={`w-full mt-4 rounded-lg`} style={{ maxWidth: "100%" }}>
                                    {result === true ? (
                                        <>
                                            <div
                                                className="mt-[15px] box-item-left p-4 bg-emerald-50 border border-emerald-100 rounded-lg"
                                                dangerouslySetInnerHTML={{ __html: currentQuestion.rationale }}
                                            />
                                        </>
                                    ) : result === false ? (
                                        <>
                                            <div
                                                className="mt-[15px] box-item-left p-4 bg-rose-50 border border-rose-100 rounded-lg"
                                                dangerouslySetInnerHTML={{ __html: currentQuestion.rationale }}
                                            />
                                        </>
                                    ) : (
                                        <span></span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Mobile Version: 세로 레이아웃 (문제 영역: 상단 50%, 옵션/해설 영역: 하단 50%) */}
        <div className="flex flex-col md:hidden h-screen">
            {/* 통합된 스크롤 영역 */}
            <div className="h-full pb-[30px]">
                {/* Question Content */}
                <div className="mx-2 mt-2" data-highlightable="true">
                    <div className="flex flex-col justify-start mx-2 box-item-left p-3 rounded-lg">
                        {sat === "Math" || sat === "math" ? (
                            <div
                                id="test-imgsat"
                                dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                                className="m-2 stem-content"
                                style={{
                                    minWidth: 0,
                                    width: "fit-content",
                                    maxWidth: "100%",
                                    overflow: "visible"
                                }}
                            />
                        ) : (
                            <div
                                dangerouslySetInnerHTML={{ __html: currentQuestion.stimulus }}
                                className="m-2 stem-content"
                                style={{
                                    minWidth: 0,
                                    width: "fit-content",
                                    maxWidth: "100%",
                                    overflow: "visible"
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Options Area */}
                <div className="px-4 mt-3">
                    {/* Question Header - 스티키 포지션 적용 */}
                    <div className="top-0 z-10 pt-2 sticky">
                        <div
                            className="flex flex-row justify-between rounded-[12px] shadow-sm mx-2 mb-3"
                            style={
                                result === true
                                    ? { background: "linear-gradient(to right, #e0f2fe, #f0fdfa)" }
                                    : result === false
                                        ? { background: "linear-gradient(to right, #fef2f2, #fff1f2)" }
                                        : { background: "linear-gradient(to right, #f8fafc, #f1f5f9)" }
                            }
                        >
                            <div className="py-2 flex flex-row gap-2">
                                <div className="text-gray-700 pl-4 flex flex-row items-center gap-1 font-medium">
                                    Q{currentQuestion.index + 1}
                                    {!hideBookmark && (
                                        <motion.div
                                            whileTap={{ scale: 0.97 }}
                                            onClick={() => handleBookMark(currentQuestion.questionIds)}
                                            className="m-1 cursor-pointer"
                                        >
                                            {currentQuestion.bookMarked ? (
                                                <BsBookmarkFill color="#2563eb" size="1.2rem" />
                                            ) : (
                                                <BsBookmark color="#64748b" size="1.2rem" />
                                            )}
                                        </motion.div>
                                    )}
                                    {!hideBookmark && (
                                        <div>
                                            <p>Mark For Review</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-row items-center pr-4">
                                <motion.div
                                    whileTap={{ scale: 0.98 }}
                                    className={`p-[5px] border-gray-300 border-[1px] rounded-md cursor-pointer mr-4 transition-colors ${showCrossOutMenu
                                            ? "bg-blue-500 text-white border-blue-500"
                                            : "bg-white hover:bg-gray-50"
                                        }`}
                                    onClick={() => setShowCrossOutMenu((pre) => !pre)}
                                >
                                    <ImSpellCheck className={showCrossOutMenu ? "text-white" : "text-gray-600"} />
                                </motion.div>
                            </div>
                        </div>
                        {result === true ? (
                            <div className="flex mx-4 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-[8px] p-2 mb-[10px] shadow-sm">
                                <MdOutlineTimer className="mt-[4px] ml-[5px] mr-[5px] text-emerald-600" />
                                <span className="font-medium">Time Spent: {Math.max(1, time ?? 0)}sec</span>
                            </div>
                        ) : result === false ? (
                            <div className="flex mx-4 bg-rose-100 text-rose-800 border border-rose-200 rounded-[8px] p-2 mb-[10px] shadow-sm">
                                <MdOutlineTimer className="mt-[4px] ml-[5px] mr-[5px] text-rose-600" />
                                <span className="font-medium">Time Spent: {Math.max(1, time ?? 0)}sec</span>
                            </div>
                        ) : (
                            <span></span>
                        )}
                    </div>

                    {/* 추가 stem 영역 (Math가 아닌 경우) */}
                    {sat !== "Math" && sat !== "math" && (
                        <div className="p-4 bg-white box-item-left text-center rounded-lg shadow-sm mb-3 border border-gray-100">
                            <div
                                dangerouslySetInnerHTML={{ __html: currentQuestion.stem }}
                                style={{
                                    minWidth: 0,
                                    width: "fit-content",
                                    maxWidth: "100%",
                                    overflow: "visible"
                                }}
                            />
                        </div>
                    )}

                    {/* Options 영역 */}
                    <div className="options-container mt-2 mb-3" data-highlightable="true">
                        {currentQuestion.a !== null && currentQuestion.a !== "null" ? (
                            <>
                                <Option
                                    handleCrossOutOptions={handleCrossOutOptions}
                                    currentQuestion={currentQuestion}
                                    handleSelectOption={handleSelectOption}
                                    idx={0}
                                    showCrossOutMenu={showCrossOutMenu}
                                    crossedOutOptions={crossedOutOptions}
                                    data={currentQuestion.a}
                                    result={result}
                                />
                                <Option
                                    handleCrossOutOptions={handleCrossOutOptions}
                                    currentQuestion={currentQuestion}
                                    handleSelectOption={handleSelectOption}
                                    idx={1}
                                    showCrossOutMenu={showCrossOutMenu}
                                    crossedOutOptions={crossedOutOptions}
                                    data={currentQuestion.b}
                                    result={result}
                                />
                                <Option
                                    handleCrossOutOptions={handleCrossOutOptions}
                                    currentQuestion={currentQuestion}
                                    handleSelectOption={handleSelectOption}
                                    idx={2}
                                    showCrossOutMenu={showCrossOutMenu}
                                    crossedOutOptions={crossedOutOptions}
                                    data={currentQuestion.c}
                                    result={result}
                                />
                                <Option
                                    handleCrossOutOptions={handleCrossOutOptions}
                                    currentQuestion={currentQuestion}
                                    handleSelectOption={handleSelectOption}
                                    idx={3}
                                    showCrossOutMenu={showCrossOutMenu}
                                    crossedOutOptions={crossedOutOptions}
                                    data={currentQuestion.d}
                                    result={result}
                                />
                            </>
                        ) : (
                            <input
                                className={`w-full py-2 px-3 outline-none rounded-md transition-colors duration-200 ${result === true
                                        ? "bg-white border-2 border-emerald-300 focus:border-emerald-500"
                                        : result === false
                                            ? "bg-white border-2 border-rose-300 focus:border-rose-500"
                                            : "border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                    }`}
                                type="text"
                                value={currentQuestion.selectedOption || ""}
                                disabled={result === true || result === false}  // 결과가 true/false이면 수정 불가
                                readOnly={result === true || result === false}   // 추가적으로 읽기 전용 처리
                                onChange={(e) => {
                                    const cleaned = e.target.value.replace(/[^-0-9./]/g, "");
                                    handleAnswerValueChange(currentQuestion.questionIds, cleaned);
                                }}
                            />
                        )}
                    </div>

                    {/* Rationale Area */}
                    {(result === true || result === false) && (
                        <div
                            className={`mt-4 w-full max-w-lg p-4 rounded-lg shadow-sm ${result === true
                                    ? "bg-emerald-50 border border-emerald-100"
                                    : "bg-rose-50 border border-rose-100"
                                }`}
                        >
                            <div
                                className="box-item-left"
                                dangerouslySetInnerHTML={{ __html: currentQuestion.rationale }}
                                style={{
                                    minWidth: 0,
                                    width: "fit-content",
                                    maxWidth: "100%",
                                    overflow: "visible"
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    </>

    )
};

export default Question;

// Option component remains the same
const Option = ({
    handleCrossOutOptions,
    currentQuestion,
    idx,
    handleSelectOption,
    showCrossOutMenu,
    crossedOutOptions,
    data,
    result
}) => {

    return (
        <div className="flex flex-row gap-1">
            <motion.div
                whileTap={{ scale: 0.98 }}
                className={`flex flex-row w-full rounded-[12px] transition-colors duration-200 ease-in-out
                    ${currentQuestion.selectedOption === getOptionLetter(idx).toLowerCase()
                        ? "bg-blue-100 border-blue-300 shadow-sm"
                        : "bg-gray-100 border-gray-200 hover:bg-gray-50"
                    } 
                    ${currentQuestion.selectedOption === getOptionLetter(idx).toLowerCase()
                        ? "border-[1px]"
                        : "border-[1px]"
                    } 
                    gap-2 p-2 m-3 cursor-pointer relative 
                    ${currentQuestion.crossedOutOptions.includes(getOptionLetter(idx).toLowerCase()) &&
                    "before:absolute before:bg-gray-500 before:w-[103%] before:h-[2px] before:top-[50%] before:-left-[6px]"
                    }`}
                onClick={() => {
                    if (result === true || result === false) return;
                    handleSelectOption(
                        currentQuestion.questionIds,
                        getOptionLetter(idx).toLowerCase()
                    );
                }}
            >
                <div className="ml-2 p-1 flex flex-row items-center">
                    <p className={`flex flex-row items-center justify-center p-1 w-7 h-7 border-[1px] rounded-full text-sm font-medium
                        ${currentQuestion.selectedOption === getOptionLetter(idx).toLowerCase()
                            ? "border-blue-500 bg-blue-500 text-white"
                            : "border-gray-400 bg-white text-gray-700"
                        }`}>
                        {getOptionLetter(idx)}
                    </p>
                </div>
                <div className="flex flex-row items-center p-[2px]">
                    {/* Use the Latex component to render LaTeX within the option */}
                    <Latex>{data}</Latex>
                </div>
            </motion.div>
            {showCrossOutMenu && (
                <motion.div
                    className="flex flex-row items-center"
                    whileTap={{ scale: 0.97 }}
                >
                    <div className="relative">
                        {currentQuestion.crossedOutOptions.includes(
                            getOptionLetter(idx).toLowerCase()
                        ) ? (
                            <p
                                className="cursor-pointer text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                                onClick={() =>
                                    handleCrossOutOptions(
                                        currentQuestion.questionIds,
                                        getOptionLetter(idx).toLowerCase()
                                    )
                                }
                            >
                                Undo
                            </p>
                        ) : (
                            <>
                                <p
                                    onClick={() =>
                                        handleCrossOutOptions(
                                            currentQuestion.questionIds,
                                            getOptionLetter(idx).toLowerCase()
                                        )
                                    }
                                    className="flex flex-row items-center justify-center p-1 w-7 h-7 border-[1px] border-gray-400 bg-white text-gray-700 rounded-full cursor-pointer hover:bg-gray-50 transition-colors"
                                >
                                    {getOptionLetter(idx)}
                                </p>
                                <div
                                    className="absolute w-7 h-[2px] bg-rose-500 top-3.5 cursor-pointer"
                                    onClick={() =>
                                        handleCrossOutOptions(
                                            currentQuestion.questionIds,
                                            getOptionLetter(idx).toLowerCase()
                                        )
                                    }
                                ></div>
                            </>
                        )}
                    </div>
                </motion.div>
            )}
        </div>
    );
};

function getOptionLetter(idx) {
    switch (idx) {
        case 0:
            return "A";
        case 1:
            return "B";
        case 2:
            return "C";
        case 3:
            return "D";
        default:
            return "A";
    }
}
